import React, { useEffect, useMemo, useState } from "react";
import { DataTable, DataTableRowReorderEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import toast from "react-hot-toast";
import {
  useGetSupportArticlesQuery,
  useGetSupportConfigQuery,
  useReorderFeaturedSupportArticlesMutation,
  useToggleSupportArticleFeaturedMutation,
} from "../../store/services/support";
import { useTenantScope } from "../../hooks/useTenantScope";
import { SupportArticleDetailDialog } from "../../components/Dialogs/SupportArticleDetailDialog";
import { SupportArticleListItem } from "../../models/support";

const getApiError = (e: any): string => e?.data?.error?.message || "Ocurrió un error";

// Búsqueda tolerante a mayúsculas y acentos: "articulo" tiene que encontrar "Artículo".
const normalizeText = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

export function SupportFaqs() {
  const tenantScope = useTenantScope();
  // Con scope global ("Todos") no hay un único tenant a ordenar: el usuario debe
  // elegir uno en el selector superior, igual que en el catálogo global de beneficios.
  const needsTenantSelection = !tenantScope.loading && !tenantScope.locked;

  const { data: articles, isLoading: isLoadingArticles } = useGetSupportArticlesQuery(
    { isActive: true },
    { skip: tenantScope.loading || needsTenantSelection }
  );
  const { data: config, isLoading: isLoadingConfig } = useGetSupportConfigQuery(undefined, {
    skip: tenantScope.loading || needsTenantSelection,
  });

  const [toggleFeatured, { isLoading: isToggling }] = useToggleSupportArticleFeaturedMutation();
  const [reorderFeatured, { isLoading: isReordering }] =
    useReorderFeaturedSupportArticlesMutation();

  const [featuredRows, setFeaturedRows] = useState<SupportArticleListItem[]>([]);
  const [articleToView, setArticleToView] = useState<SupportArticleListItem>();
  const [availableSearch, setAvailableSearch] = useState<string>("");

  useEffect(() => {
    if (!articles) {
      setFeaturedRows([]);
      return;
    }
    setFeaturedRows(
      articles
        .filter((article) => article.isFeatured)
        .sort((a, b) => (a.featuredOrder ?? 0) - (b.featuredOrder ?? 0))
    );
  }, [articles]);

  const availableArticles = useMemo(() => {
    const term = normalizeText(availableSearch);
    return (articles ?? [])
      .filter((article) => !article.isFeatured)
      .filter((article) => !term || normalizeText(article.title).includes(term));
  }, [articles, availableSearch]);

  const maxFeatured = config?.maxFeaturedFaqs ?? 0;
  const isAtMax = maxFeatured > 0 && featuredRows.length >= maxFeatured;
  const isMutating = isToggling || isReordering;

  const handleRowReorder = (e: DataTableRowReorderEvent<SupportArticleListItem[]>) => {
    setFeaturedRows(e.value);
    reorderFeatured({
      article_ids: e.value.map((article) => article.id),
      ...(!tenantScope.hidden && { tenant_id: tenantScope.tenantId }),
    })
      .unwrap()
      .then(() => {
        toast.success("Orden de FAQs actualizado");
      })
      .catch((error) => {
        toast.error(getApiError(error));
      });
  };

  const handleRemove = (article: SupportArticleListItem) => {
    toggleFeatured({ id: article.id, is_featured: false })
      .unwrap()
      .then(() => {
        toast.success("Artículo quitado de las FAQs destacadas");
      })
      .catch((error) => {
        toast.error(getApiError(error));
      });
  };

  const handleFeature = (article: SupportArticleListItem) => {
    toggleFeatured({ id: article.id, is_featured: true })
      .unwrap()
      .then(() => {
        toast.success("Artículo destacado como FAQ");
      })
      .catch((error) => {
        toast.error(getApiError(error));
      });
  };

  const categoryBodyTemplate = (row: SupportArticleListItem) => row.categoryName || "—";

  const detailBodyTemplate = (row: SupportArticleListItem) => (
    <Button
      icon="pi pi-eye"
      rounded
      outlined
      size="small"
      tooltip="Ver detalle"
      tooltipOptions={{ position: "top" }}
      onClick={() => setArticleToView(row)}
    />
  );

  const feedbackBodyTemplate = (row: SupportArticleListItem) =>
    row.feedbackPct === null ? (
      <span className="text-500">—</span>
    ) : (
      <Tag severity="info" value={`${row.feedbackPct}% útil`} />
    );

  if (isLoadingArticles || isLoadingConfig || tenantScope.loading) {
    return (
      <div
        className="flex justify-content-center align-items-center"
        style={{ height: "200px" }}
      >
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="p-card gap-4 p-3">
      <div className="flex w-full justify-content-center align-items-center gap-3 mt-4 mb-6">
        <h1 className="text-center m-0">FAQs destacadas</h1>
      </div>

      {needsTenantSelection ? (
        <div className="flex justify-content-center p-4">
          <Message
            severity="warn"
            text="Seleccioná un tenant en el selector superior para ver y ordenar sus FAQs destacadas"
          />
        </div>
      ) : (
        <div className="grid">
          <div className="col-12">
            <h3 className="text-xl font-semibold text-900 mb-2">
              <i className="pi pi-star-fill mr-2 text-primary"></i>
              FAQs destacadas ({featuredRows.length}/{maxFeatured || "—"})
            </h3>
            <small className="text-600 block mb-3">
              Arrastrá las filas para cambiar el orden en que se muestran en la app.
            </small>

            {isAtMax && (
              <Message
                severity="warn"
                className="w-full mb-3"
                text={`Alcanzaste el máximo de ${maxFeatured} FAQs destacadas`}
              />
            )}

            <DataTable
              value={featuredRows}
              reorderableRows
              onRowReorder={handleRowReorder}
              loading={isMutating}
              tableStyle={{ minWidth: "50rem" }}
              responsiveLayout="scroll"
              emptyMessage="Todavía no hay FAQs destacadas."
            >
              <Column rowReorder style={{ width: "3rem" }} />
              <Column
                header="#"
                body={(_row: SupportArticleListItem, options) => options.rowIndex + 1}
                style={{ width: "4rem" }}
              />
              <Column field="title" header="Título" style={{ minWidth: "20rem" }} />
              <Column header="Categoría" body={categoryBodyTemplate} style={{ width: "12rem" }} />
              <Column header="% Útil" body={feedbackBodyTemplate} style={{ width: "9rem" }} />
              <Column
                header="Detalle"
                body={detailBodyTemplate}
                style={{ width: "7rem" }}
              />
              <Column
                header="Acciones"
                body={(row: SupportArticleListItem) => (
                  <Button
                    label="Quitar"
                    icon="pi pi-times"
                    severity="danger"
                    outlined
                    size="small"
                    disabled={isMutating}
                    onClick={() => handleRemove(row)}
                  />
                )}
                style={{ width: "9rem" }}
              />
            </DataTable>
          </div>

          <div className="col-12 mt-5">
            <h3 className="text-xl font-semibold text-900 mb-3">
              <i className="pi pi-list mr-2 text-primary"></i>
              Artículos disponibles
            </h3>

            <div className="flex flex-column gap-2 mb-3">
              <label htmlFor="availableSearch" className="text-600 text-sm">
                Buscar
              </label>
              <IconField iconPosition="left" className="w-full" style={{ maxWidth: "20rem" }}>
                <InputIcon className="pi pi-search" />
                <InputText
                  id="availableSearch"
                  value={availableSearch}
                  onChange={(e) => setAvailableSearch(e.target.value)}
                  placeholder="Buscar por título"
                  className="w-full"
                />
              </IconField>
            </div>

            <DataTable
              value={availableArticles}
              loading={isMutating}
              tableStyle={{ minWidth: "50rem" }}
              paginator
              rows={10}
              rowsPerPageOptions={[5, 10, 25, 50]}
              responsiveLayout="scroll"
              emptyMessage={
                availableSearch.trim()
                  ? "No hay artículos que coincidan con la búsqueda."
                  : "No hay artículos activos disponibles."
              }
            >
              <Column field="title" header="Título" style={{ minWidth: "20rem" }} />
              <Column header="Categoría" body={categoryBodyTemplate} style={{ width: "12rem" }} />
              <Column header="% Útil" body={feedbackBodyTemplate} style={{ width: "9rem" }} />
              <Column
                header="Detalle"
                body={detailBodyTemplate}
                style={{ width: "7rem" }}
              />
              <Column
                header="Acciones"
                body={(row: SupportArticleListItem) => (
                  <Button
                    label="Destacar"
                    icon="pi pi-star"
                    outlined
                    size="small"
                    disabled={isMutating || isAtMax}
                    tooltip={isAtMax ? "Ya alcanzaste el máximo de FAQs destacadas" : undefined}
                    tooltipOptions={{ position: "top", showOnDisabled: true }}
                    onClick={() => handleFeature(row)}
                  />
                )}
                style={{ width: "10rem" }}
              />
            </DataTable>
          </div>
        </div>
      )}

      <SupportArticleDetailDialog
        article={articleToView}
        onHide={() => setArticleToView(undefined)}
      />
    </div>
  );
}
